'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink, Loader2, Receipt, Send, SquarePen, Edit3, Trash2, X } from 'lucide-react';
import { authService } from '@/lib/authService';
import { useAuth } from '@/hooks/useAuth';
import { useWebSocketChannel } from '@/hooks/useWebSocketChannel';
import { useTheme } from '@/contexts/ThemeContext';
import { confirm, notify } from '@/lib/notifications';
import { UserAvatar, UserIdentityLink, getUserDisplayName } from '@/components/work-items/WorkItemUserIdentity';
import {
  FormattedCommentBody,
  getCommentSourceBadgeMeta,
  extractMentionUserIdsFromCommentText,
  type CommentSourceReferenceKind,
} from '@/components/work-items/commentPresentation';
import { useWorkItemCommentMentions } from '@/components/work-items/useWorkItemCommentMentions';
import { isBillingWorkItemComment, type WorkItemCommentType } from '@/lib/work-items/commentTypes';
import { hasPrivilegedCommentRole } from '@/lib/work-items/commentAccess';
import { isCapacitor } from '@/lib/platformUtils';

const getAuthToken = (): string => authService.getTokens()?.access_token || '';
const BILLING_COMMENT_COMPOSER_OVERLAY_Z_CLASS = 'z-[200]';

interface BillingCommentRow {
  id: string;
  work_item_id: string;
  user_id: number;
  user_name: string;
  user_email?: string;
  user_display_name?: string;
  comment_text: string;
  is_internal?: boolean;
  comment_type: WorkItemCommentType;
  created_at: string;
  updated_at: string;
  source_appointment_id?: string | null;
  source_appointment_number?: string | null;
  source_reference_kind?: CommentSourceReferenceKind | null;
}

export interface BillingCommentsSectionProps {
  workItemId: string;
  isActive: boolean;
  includeRelated?: boolean;
  /** @deprecated Rows use API permissions + ownership; retained for callers. */
  listReadOnly?: boolean;
  readOnly?: boolean;
  /** Tighter spacing when embedded under timers / billing / expenses. */
  embedded?: boolean;
  mentionTeamId?: string | null;
  enableMentions?: boolean;
  /** Optional line item ID for line-item-specific comments */
  lineItemId?: string | null;
  /** Context type for comment filtering (e.g., 'document', 'line_item') */
  context?: string;
  /** Callback when a comment is added */
  onCommentAdded?: () => void;
}

function formatCommentDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

async function parseJsonResponse<T>(response: Response): Promise<T | null> {
  const text = await response.text().catch(() => '');
  const trimmed = text.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed) as T;
  } catch {
    return null;
  }
}

export default function BillingCommentsSection({
  workItemId,
  isActive,
  includeRelated = false,
  listReadOnly: _listReadOnly = false,
  readOnly = false,
  embedded = false,
  mentionTeamId = null,
  enableMentions = true,
  lineItemId = null,
  context,
  onCommentAdded,
}: BillingCommentsSectionProps) {
  void _listReadOnly;
  const { user, isRoot } = useAuth();
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  const currentUserId = user?.id != null ? Number(user.id) : null;
  const clientId = user?.client_id ? String(user.client_id) : null;

  const [comments, setComments] = useState<BillingCommentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [draft, setDraft] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);
  const [editingComment, setEditingComment] = useState<BillingCommentRow | null>(null);
  const [apiCanModerate, setApiCanModerate] = useState(false);
  const loadRequestIdRef = useRef(0);
  const realtimeRefreshTimeoutRef = useRef<number | null>(null);
  const canCompose = !readOnly;
  const canModerateComments = apiCanModerate || hasPrivilegedCommentRole(user) || isRoot;

  const {
    textareaRef,
    mentionMenuOpen,
    mentionMembersLoading,
    filteredMentionSuggestions,
    mentionHighlight,
    mentionCandidates,
    handleComposerChange,
    handleComposerCaretMove,
    applyMentionPick,
    handleMentionKeyDown,
    ingestMentionTeamFromCommentsApi,
  } = useWorkItemCommentMentions({
    workItemId,
    mentionTeamIdProp: mentionTeamId,
    enableMentions,
    currentUserId,
    getAuthToken,
    mentionUiEnabled: canCompose && composerOpen,
    draft,
    setDraft,
  });

  const loadComments = useCallback(async () => {
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    setLoading(true);

    try {
      const params = new URLSearchParams();
      if (includeRelated) params.append('include_related', 'true');
      if (lineItemId) params.append('line_item_id', lineItemId);
      const queryString = params.toString();
      const response = await fetch(`/api/v1/work-items/${workItemId}/comments${queryString ? `?${queryString}` : ''}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });

      if (loadRequestIdRef.current !== requestId) {
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to load comments');
      }

      const data = await parseJsonResponse<{
        success?: boolean;
        data?: BillingCommentRow[];
        mention_team_id?: string | null;
        can_moderate_comments?: boolean;
      }>(response);

      if (!data?.success || !Array.isArray(data.data)) {
        setComments([]);
        setApiCanModerate(false);
        ingestMentionTeamFromCommentsApi(null);
        return;
      }

      setApiCanModerate(Boolean(data.can_moderate_comments));
      ingestMentionTeamFromCommentsApi(
        typeof data.mention_team_id === 'string' && data.mention_team_id.trim().length > 0
          ? data.mention_team_id.trim()
          : null,
      );

      const billingOnly = data.data
        .filter((row) => isBillingWorkItemComment(row))
        .map((row) => ({
          ...row,
          comment_type: row.comment_type || 'billing',
        }))
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      setComments(billingOnly);
    } catch (e) {
      console.error('[BillingCommentsSection] load failed', e);
      notify.error('Could not load billing comments');
      setComments([]);
      setApiCanModerate(false);
      ingestMentionTeamFromCommentsApi(null);
    } finally {
      if (loadRequestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [includeRelated, workItemId, lineItemId, ingestMentionTeamFromCommentsApi]);

  useEffect(() => {
    setComments([]);
    setDraft('');
    setEditingComment(null);
    setComposerOpen(false);
  }, [workItemId]);

  useEffect(() => {
    if (!isActive) {
      return;
    }
    void loadComments();
  }, [isActive, loadComments, workItemId]);

  useEffect(() => {
    if (!composerOpen) {
      return;
    }
    const t = window.setTimeout(() => textareaRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [composerOpen, editingComment, textareaRef]);

  const scheduleRealtimeRefresh = useCallback(() => {
    if (!isActive) {
      return;
    }

    if (typeof window === 'undefined') {
      void loadComments();
      return;
    }

    if (realtimeRefreshTimeoutRef.current) {
      window.clearTimeout(realtimeRefreshTimeoutRef.current);
    }

    realtimeRefreshTimeoutRef.current = window.setTimeout(() => {
      realtimeRefreshTimeoutRef.current = null;
      void loadComments();
    }, 120);
  }, [isActive, loadComments]);

  useWebSocketChannel({
    channel: clientId ? `operations:client:${clientId}` : undefined,
    autoConnect: true,
    onMessage: (data) => {
      const message = (data || {}) as Record<string, unknown>;
      const type = typeof message.type === 'string' ? message.type : '';
      if (
        type.startsWith('appointment_')
        || type.startsWith('work_item_')
        || type === 'notification'
        || type === 'operations_refresh'
      ) {
        scheduleRealtimeRefresh();
      }
    },
  });

  useEffect(() => {
    return () => {
      if (realtimeRefreshTimeoutRef.current && typeof window !== 'undefined') {
        window.clearTimeout(realtimeRefreshTimeoutRef.current);
      }
    };
  }, []);

  const closeComposer = useCallback(() => {
    setComposerOpen(false);
    setEditingComment(null);
    setDraft('');
  }, []);

  const openNewComment = useCallback(() => {
    setEditingComment(null);
    setDraft('');
    setComposerOpen(true);
  }, []);

  const openEditComment = useCallback((row: BillingCommentRow) => {
    setEditingComment(row);
    setDraft(row.comment_text);
    setComposerOpen(true);
  }, []);

  const submit = useCallback(async () => {
    const text = draft.trim();
    const mentionIds = extractMentionUserIdsFromCommentText(text);

    if (!text || submitting) {
      return;
    }

    try {
      setSubmitting(true);
      const targetId = editingComment ? String(editingComment.work_item_id) : String(workItemId);
      const params = new URLSearchParams();
      if (editingComment) params.append('comment_id', editingComment.id);
      if (lineItemId) params.append('line_item_id', lineItemId);
      const queryString = params.toString();
      const url = `/api/v1/work-items/${targetId}/comments${queryString ? `?${queryString}` : ''}`;

      const payloadBody = editingComment
        ? { comment_text: text, comment_type: 'billing' as const, is_internal: true }
        : {
            comment_text: text,
            comment_type: 'billing' as const,
            is_internal: true,
            ...(lineItemId ? { line_item_id: lineItemId } : {}),
            ...(mentionIds.length > 0 ? { mentioned_user_ids: mentionIds } : {}),
          };

      const response = await fetch(url, {
        method: editingComment ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payloadBody),
      });

      const payload = await parseJsonResponse<{ success?: boolean; error?: string }>(response);

      if (!response.ok || !payload?.success) {
        throw new Error(payload?.error || 'Failed to save billing comment');
      }

      closeComposer();
      await loadComments();
      notify.success(editingComment ? 'Billing comment updated' : 'Billing comment saved');
      onCommentAdded?.();
    } catch (e) {
      console.error('[BillingCommentsSection] save failed', e);
      notify.error(e instanceof Error ? e.message : 'Failed to save billing comment');
    } finally {
      setSubmitting(false);
    }
  }, [closeComposer, draft, editingComment, loadComments, submitting, workItemId, lineItemId, onCommentAdded]);

  const deleteComment = useCallback(
    async (row: BillingCommentRow) => {
      await confirm({
        title: 'Delete billing comment',
        message: 'Remove this billing note? This cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        variant: 'danger',
        onConfirm: async () => {
          try {
            const targetId = String(row.work_item_id);
            const response = await fetch(`/api/v1/work-items/${targetId}/comments?comment_id=${row.id}`, {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${getAuthToken()}` },
            });
            const payload = await parseJsonResponse<{ success?: boolean; error?: string }>(response);
            if (!response.ok || payload?.success === false) {
              throw new Error(payload?.error || 'Failed to delete comment');
            }
            await loadComments();
            notify.success('Comment deleted');
          } catch (e) {
            console.error('[BillingCommentsSection] delete failed', e);
            notify.error(e instanceof Error ? e.message : 'Failed to delete comment');
          }
        },
      });
    },
    [loadComments],
  );

  const badgeClass = isDark ? 'bg-slate-800 text-slate-200 border border-slate-600/80' : 'bg-slate-100 text-slate-800 border border-slate-200';

  const emptyHint = useMemo(
    () =>
      includeRelated
        ? 'Notes from this job and linked appointments roll up below. Tap the pen to add one.'
        : 'Internal notes for billing hand-off. Tap the pen to add one.',
    [includeRelated],
  );

  const canEditExisting = useCallback(
    (row: BillingCommentRow): boolean => {
      if (!canCompose || readOnly) return false;
      const isOwner = currentUserId != null && Number(row.user_id) === Number(currentUserId);
      return canModerateComments || isOwner;
    },
    [canCompose, canModerateComments, currentUserId, readOnly],
  );

  /** Billing composer modal — portaled so Smart Task / nested scroll regions do not clip it */
  const composerModal =
    composerOpen && typeof document !== 'undefined'
      ? createPortal(
          <div
              className={`fixed inset-0 ${BILLING_COMMENT_COMPOSER_OVERLAY_Z_CLASS} flex items-end justify-center sm:items-center p-0 sm:p-4`}
            role="dialog"
            aria-modal="true"
            aria-label={editingComment ? 'Edit billing comment' : 'New billing comment'}
          >
            <button
              type="button"
              className="absolute inset-0 bg-black/50"
              aria-label="Close"
              onClick={() => !submitting && closeComposer()}
            />
            <div
              className={`relative w-full max-h-[min(94dvh,52rem)] overflow-hidden rounded-t-2xl border shadow-2xl sm:rounded-2xl sm:max-w-xl flex flex-col ${
                isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'
              }`}
              style={{
                paddingBottom: isCapacitor() ? 'max(12px, env(safe-area-inset-bottom))' : undefined,
              }}
            >
              <div className={`flex items-center justify-between gap-3 border-b px-4 py-3 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {editingComment ? 'Edit billing comment' : 'New billing comment'}
                </span>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => closeComposer()}
                  className={`rounded-lg p-1.5 transition ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 space-y-3">
                {enableMentions ? (
                  <p className={`text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Type @ to mention team members · Ctrl+Enter to save.</p>
                ) : null}
                {/* Mentions below the textarea: upward popovers clip inside overflow-y-auto; top-full stays in view in this modal. */}
                <div
                  className={
                    mentionMenuOpen && canCompose ? 'relative mb-56' : 'relative'
                  }
                >
                  <textarea
                    ref={textareaRef}
                    value={draft}
                    onChange={handleComposerChange}
                    onSelect={handleComposerCaretMove}
                    onClick={handleComposerCaretMove}
                    onKeyUp={handleComposerCaretMove}
                    onKeyDown={(e) => {
                      if (handleMentionKeyDown(e)) {
                        return;
                      }
                      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                        e.preventDefault();
                        void submit();
                      }
                    }}
                    rows={7}
                    placeholder="Explain what was prepared, what still needs review, or hand-off details for finance…"
                    className={`w-full rounded-lg border px-3 py-2 text-sm resize-none ${
                      isDark ? 'border-gray-600 bg-gray-800 text-gray-100 placeholder:text-gray-500' : 'border-gray-300 bg-white text-gray-900 placeholder:text-gray-400'
                    }`}
                    disabled={submitting}
                  />
                  {mentionMenuOpen && canCompose ? (
                    <div
                      className={`absolute top-full left-0 right-0 z-30 mt-1 max-h-56 overflow-y-auto rounded-lg border shadow-lg py-1 ${
                        isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                      }`}
                    >
                      {mentionMembersLoading ? (
                        <div className={`px-3 py-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading team members…</div>
                      ) : filteredMentionSuggestions.length === 0 ? (
                        <div className={`px-3 py-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {mentionCandidates.length === 0 ? 'No teammates on this team.' : 'No matching teammates.'}
                        </div>
                      ) : (
                        filteredMentionSuggestions.map((m, idx) => (
                          <button
                            key={m.user_id}
                            type="button"
                            onMouseDown={(ev) => ev.preventDefault()}
                            onClick={() => applyMentionPick(m)}
                            className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 transition-colors ${
                              idx === mentionHighlight
                                ? isDark
                                  ? 'bg-gray-700 text-white'
                                  : 'bg-blue-50 text-blue-900'
                                : isDark
                                  ? 'text-gray-200 hover:bg-gray-700/80'
                                  : 'text-gray-800 hover:bg-gray-50'
                            }`}
                          >
                            <span className="truncate">{m.label}</span>
                          </button>
                        ))
                      )}
                    </div>
                  ) : null}
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={() => closeComposer()}
                    className={`rounded-lg px-3 py-1.5 text-xs font-medium ${isDark ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void submit()}
                    disabled={submitting || !draft.trim()}
                    className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold text-white transition ${
                      submitting || !draft.trim() ? 'bg-blue-900/40 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      className={embedded ? 'mt-8 pt-6 border-t border-dashed ' + (isDark ? 'border-gray-700' : 'border-gray-200') : 'space-y-4'}
      data-tour-id="billing-comments-section"
    >
      {!embedded && (
        <div className={`rounded-xl border px-3 py-2.5 ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50/80'}`}>
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{emptyHint}</p>
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <h4 className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Billing comments</h4>
        {canCompose && (
          <button
            type="button"
            onClick={() => openNewComment()}
            title="Add billing comment"
            className={`inline-flex h-9 w-9 items-center justify-center rounded-xl border transition ${
              isDark ? 'border-gray-600 bg-gray-800 text-gray-200 hover:bg-gray-700' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            }`}
            aria-label="Add billing comment"
          >
            <SquarePen className="h-4 w-4" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        </div>
      ) : comments.length === 0 ? (
        <div className={`text-center py-6 rounded-xl border ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
          <Receipt className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No billing comments yet</p>
        </div>
      ) : (
        <div className={`rounded-xl border divide-y max-h-[min(40vh,20rem)] overflow-y-auto overscroll-y-contain ${isDark ? 'border-gray-700 divide-gray-800 bg-gray-900' : 'border-gray-200 divide-gray-100 bg-white'}`}>
          {comments.map((comment) => {
            const sourceKind: CommentSourceReferenceKind = comment.source_reference_kind === 'group' ? 'group' : 'appointment';
            const sourceBadgeMeta = getCommentSourceBadgeMeta(sourceKind, isDark);
            const showAptBadge = Boolean(comment.source_appointment_number) && String(comment.work_item_id) !== String(workItemId);
            const allowRowActions = canEditExisting(comment);

            return (
              <div key={comment.id} className="px-3 sm:px-4 py-3">
                <div className="flex gap-3">
                  <UserAvatar
                    user={{
                      userId: comment.user_id,
                      displayName: comment.user_display_name || comment.user_name,
                      email: comment.user_email,
                    }}
                    size="md"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <UserIdentityLink
                        user={{
                          userId: comment.user_id,
                          displayName: getUserDisplayName({
                            displayName: comment.user_display_name || comment.user_name,
                            email: comment.user_email,
                          }),
                          email: comment.user_email,
                        }}
                        isDark={isDark}
                        showAvatar={false}
                        showExternalIcon
                      />
                      <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${badgeClass}`}>
                        <span className="inline-flex items-center gap-1">
                          <Receipt size={10} />
                          Billing
                        </span>
                      </span>
                      <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{formatCommentDateTime(comment.created_at)}</span>
                      {showAptBadge && comment.source_appointment_number && (
                        <>
                          {comment.source_appointment_id ? (
                            <button
                              type="button"
                              onClick={() => {
                                if (typeof window === 'undefined' || !comment.source_appointment_id) return;
                                window.dispatchEvent(
                                  new CustomEvent('openWorkItem', {
                                    detail: { id: comment.source_appointment_id },
                                  }),
                                );
                              }}
                              className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-full inline-flex items-center gap-1 transition-colors hover:opacity-90 ${sourceBadgeMeta.className}`}
                              title={`Open ${sourceBadgeMeta.titleLabel} ${comment.source_appointment_number}`}
                            >
                              <span className="rounded-full bg-black/10 px-1 py-0.5 text-[9px] font-bold leading-none dark:bg-white/10">{sourceBadgeMeta.shortLabel}</span>
                              <span>{comment.source_appointment_number}</span>
                              <ExternalLink size={10} />
                            </button>
                          ) : (
                            <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-full inline-flex items-center gap-1 ${sourceBadgeMeta.className}`}>
                              <span className="rounded-full bg-black/10 px-1 py-0.5 text-[9px] font-bold leading-none dark:bg-white/10">{sourceBadgeMeta.shortLabel}</span>
                              {comment.source_appointment_number}
                            </span>
                          )}
                        </>
                      )}
                      {allowRowActions ? (
                        <div className="ml-auto flex items-center gap-0.5">
                          <button
                            type="button"
                            title="Edit"
                            onClick={() => openEditComment(comment)}
                            className={`rounded p-1 ${isDark ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'}`}
                            aria-label="Edit comment"
                          >
                            <Edit3 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            title="Delete"
                            onClick={() => void deleteComment(comment)}
                            className={`rounded p-1 text-red-500 ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
                            aria-label="Delete comment"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : null}
                    </div>
                    <FormattedCommentBody
                      text={comment.comment_text}
                      className="mt-1 text-sm break-words"
                      paragraphClassName={isDark ? 'text-gray-300' : 'text-gray-700'}
                      listClassName={isDark ? 'text-gray-300' : 'text-gray-700'}
                      listItemClassName={isDark ? 'text-gray-300' : 'text-gray-700'}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {composerModal}
    </div>
  );
}
